/* eslint-disable no-unused-vars */
// pages/index.js — APP SHELL (V2)
// ═══════════════════════════════════════════════════════════
// PROTECTED FILE — max 300 lines. Auth + router + feature slots.
// No feature logic. Read docs/ARCHITECTURE.md before touching.
// ═══════════════════════════════════════════════════════════
import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import Head from 'next/head';
import { AuthLoading, NoSession, SetupScreen } from '../features/auth-screens';
import AppSidebar from '../features/app-sidebar';
import ChatFeature from '../features/chat-feature';
import ArtifactLibrary from '../features/artifact-library';
// Space panel deleted (Phase 1+2 cleanup) — Workspace is the unified surface.
// shared_spaces table survives because Workspace uses it.
import Workspace from '../features/workspace';
import ChatWorkspaceFeature from '../features/chat-workspace-feature';
import WorkspaceSoloModal from '../components/workspace-solo-modal';
import GlobalOverlays from '../components/global-overlays';
import ReferralModal from '../components/referral-modal';
import SettingsSlot from '../components/settings-slot';
import ProfileFeature from '../features/profile-feature';
// Innovate panel deleted (Phase 4 cleanup) — moved into Workspace + Notes.
import NotesFeature from '../features/notes-feature';
import PlaybookFeature from '../features/playbook-feature';
import CollaborateRoom from '../features/collaborate-room';
import IntelligenceFeature from '../features/intelligence-feature';
import DomainIntelligenceFeature from '../features/intelligence';
import WorkstationsFeature from '../features/workstations';
import ProjectsFeature from '../features/projects';
import LabFeature from '../features/lab-feature';
// Ideas panel deleted (Phase 1+2 cleanup) — Ideas data type lives under
// Notes (note_type='idea'). Quick Capture saves to Notes.
import { useAuth } from '../hooks/use-auth';
import { useSpace } from '../hooks/use-space-v2';
import { useShellActions } from '../hooks/use-shell-actions';
import { generateId } from '../lib/render-helpers';

const supabase = createClient(
  process.env.NEXT_PUBLIC_SUPABASE_URL,
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
);

export default function Home() {
  // ── 1. Auth + user record ──────────────────────────
  const {
    session, authLoading,
    userRecord, setUserRecord,
  } = useAuth(supabase);

  // ── 2. Router ─────────────────────────────────────���
  const [activeFeature, setActiveFeature] = useState(() => {
    if (typeof window === 'undefined') return 'chat';
    return localStorage.getItem('shift_active_feature') || 'chat';
  });
  // innovateRemountKey removed (Phase 4 cleanup).
  const [artifactsRemountKey, setArtifactsRemountKey] = useState(0);

  // ── 3. UI toggles ─────────────────────────────────
  const [badges, setBadges] = useState({});
  const [showSearch, setShowSearch] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showSoloModal, setShowSoloModal] = useState(false);
  const [creatingSolo, setCreatingSolo] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showCapture, setShowCapture] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('shift_sidebar_collapsed') === 'true';
  });
  const [settingsTab, setSettingsTab] = useState(() => {
    if (typeof window === 'undefined') return 'account';
    return localStorage.getItem('shift_settings_tab') || 'account';
  });
  const [isIncognitoThread, setIsIncognitoThread] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('shift_incognito') === 'true';
  });
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    if (typeof window === 'undefined') return true;
    const stored = localStorage.getItem('shift_voice_enabled');
    return stored === null ? true : stored === 'true';
  });

  // ── 4. Thread state ────────────────────────────────
  const [threadHistory, setThreadHistory] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(() => generateId());
  const [pendingThreadId, setPendingThreadId] = useState(null);
  const [pendingNewThread, setPendingNewThread] = useState(false);
  const userId = session?.user?.id;
  const chatRef = useRef(null);

  // ── 5. Space hook ──────────────────────────────────
  const [spaceDefaultTab, setSpaceDefaultTab] = useState(null);
  const spaceHook = useSpace({
    session, userRecord, setUserRecord,
    supabase, generateId, setCurrentSessionId,
    setMessages: () => {},
    onInviteSent: () => {
      setShowInviteModal(false);
      setSpaceDefaultTab('Invites');
      navigate('space');
    },
  });

  // ── 6. Navigate helper ─────────────────────────────
  function navigate(feature) {
    if (feature !== 'chat' && spaceHook.activeSpaceInfo) spaceHook.closeSpaceSession();
    if (feature === 'artifacts' && activeFeature === 'artifacts') setArtifactsRemountKey(k => k + 1);
    setActiveFeature(feature);
    localStorage.setItem('shift_active_feature', feature);
  }

  // ── 7. Shell actions (extracted) ───────────────────
  const actions = useShellActions({
    session, supabase, spaceHook, setUserRecord,
    setCurrentSessionId, setPendingThreadId, setPendingNewThread,
    setThreadHistory, setIsIncognitoThread,
    setShowSoloModal, setCreatingSolo,
    currentSessionId, navigate,
  });

  const voiceConfig = {
    session, threadHistory, voiceEnabled,
    onVoiceToggle: (next) => { setVoiceEnabled(next); localStorage.setItem('shift_voice_enabled', String(next)); },
  };

  // ── Effects ────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    spaceHook.loadSharedSpace().then(() => {
      const savedSpace = localStorage.getItem('shift_active_space');
      if (savedSpace) {
        try {
          const space = JSON.parse(savedSpace);
          if (space?.id) spaceHook.openSpaceSession(space, true);
        } catch (_) { localStorage.removeItem('shift_active_space'); }
      }
    });
  }, [userId]);

  // Workspace rows (Solo OR invited multi-user Collaborate) → 'workspace'
  // feature. Non-workspace shared rooms (Space / Professional / Tutor /
  // Private created via the Space panel) → 'chat' feature which renders
  // ChatSpaceFeature. The is_workspace flag is the truth signal — Solo
  // sets it at creation, the invite API stamps it when isWorkspace=true.
  // Routing by this flag (instead of solo only) means Leave from a
  // multi-user workspace returns the inviter to the Workspace landing,
  // not into a stale chat-feature thread.
  useEffect(() => {
    if (spaceHook.activeSpaceInfo) {
      if (activeFeature === 'workspace') return;
      const isWorkspace = spaceHook.activeSpaceInfo?.is_workspace === true
        || spaceHook.activeSpaceInfo?.workspace_metadata?.solo === true;
      navigate(isWorkspace ? 'workspace' : 'chat');
    }
  }, [spaceHook.activeSpaceInfo]);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', sidebarCollapsed ? '50px' : '300px');
    localStorage.setItem('shift_sidebar_collapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => { localStorage.setItem('shift_settings_tab', settingsTab); }, [settingsTab]);
  useEffect(() => { localStorage.setItem('shift_incognito', String(isIncognitoThread)); }, [isIncognitoThread]);

  useEffect(() => {
    if (!session?.user?.id) return;
    async function loadHistory() {
      try {
        const res = await fetch('/api/user/threads', {
          headers: { 'Authorization': `Bearer ${session?.access_token || ''}` },
        });
        if (res.ok) {
          const { threads } = await res.json();
          if (threads) setThreadHistory(threads);
        }
      } catch { /* non-fatal */ }
    }
    loadHistory();
  }, [session?.user?.id]);

  // ── Auth gates ─────────────────────────────────────
  if (authLoading) return <AuthLoading />;
  if (!session) return <NoSession supabase={supabase} />;
  if (!userRecord) return <AuthLoading />;
  if (!userRecord.ai_name || !userRecord.username) return (
    <SetupScreen onSetup={actions.handleSetup} supabase={supabase} />
  );

  // ── Render ─────────────────────────────────────────
  return (
    <>
      <Head>
        <title>Shift</title>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      </Head>

      <div className="shift-app-shell">
        <AppSidebar
          activeFeature={activeFeature}
          onNavigate={navigate}
          userRecord={userRecord}
          supabase={supabase}
          badges={badges}
          threadHistory={threadHistory}
          currentSessionId={currentSessionId}
          onNewThread={() => actions.handleNewThread(false)}
          onNewIncognitoThread={() => actions.handleNewThread(true)}
          onLoadThread={actions.handleLoadThread}
          onDeleteThread={actions.deleteThread}
          onRenameThread={actions.renameThread}
          onRegenerateTitle={actions.regenerateThreadTitle}
          onSearch={() => setShowSearch(true)}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(prev => !prev)}
          onSettingsOpen={() => navigate('settings')}
          onInvite={() => setShowReferralModal(true)}
          onProfileOpen={() => navigate('profile')}
        />

        <div className="shift-main">
          {activeFeature === 'chat' && !spaceHook.activeSpaceInfo && (
            <ChatFeature
              userId={userId} supabase={supabase} userRecord={userRecord}
              currentSessionId={currentSessionId} setCurrentSessionId={setCurrentSessionId}
              threadHistory={threadHistory} setThreadHistory={setThreadHistory}
              voiceConfig={voiceConfig}
              pendingThreadId={pendingThreadId}
              onClearPendingThread={() => setPendingThreadId(null)}
              pendingNewThread={pendingNewThread}
              onClearPendingNewThread={() => setPendingNewThread(false)}
              isIncognito={isIncognitoThread}
              onLeaveIncognito={actions.handleLeaveIncognito}
              onRenameThread={actions.renameThread}
            />
          )}

          {activeFeature === 'chat' && spaceHook.activeSpaceInfo && (
            <CollaborateRoom
              userId={userId} supabase={supabase} userRecord={userRecord}
              workspaceSession={spaceHook.activeSpaceInfo}
              voiceConfig={voiceConfig}
              // Leave a chat-routed shared room → close + return to the
              // Space panel with a fresh thread id. Without the fresh id,
              // ChatFeature re-renders with the stale workspace session
              // id and shows the auto-generated incognito title for the
              // now-closed thread.
              onExit={() => {
                spaceHook.closeSpaceSession();
                setCurrentSessionId(generateId());
                navigate('space');
              }}
              onSidebarCollapse={setSidebarCollapsed}
            />
          )}

          {/* Space panel route deleted (Phase 1+2 cleanup). Workspace is
              the unified surface for shared rooms now. */}
          {activeFeature === 'workspace' && !spaceHook.activeSpaceInfo && (
            <Workspace
              session={session} isMobile={false}
              spaceHook={spaceHook}
              defaultTab={spaceDefaultTab}
              onInvite={() => { spaceHook.setInviteIsWorkspace(true); setShowInviteModal(true); }}
              onSolo={() => setShowSoloModal(true)}
              onClose={() => { setSpaceDefaultTab(null); navigate('chat'); }}
            />
          )}
          {/* Workspace split: Solo gets the private Workstation; multi-user
              (invited) workspaces get CollaborateRoom so observer mode +
              Ask Shift work. workspace_metadata.solo === true is the truth
              signal; anything else routes to Collaborate. */}
          {activeFeature === 'workspace' && spaceHook.activeSpaceInfo &&
            spaceHook.activeSpaceInfo?.workspace_metadata?.solo === true && (
            <ChatWorkspaceFeature
              ref={chatRef}
              userId={userId} supabase={supabase} userRecord={userRecord}
              currentSessionId={currentSessionId} setCurrentSessionId={setCurrentSessionId}
              threadHistory={threadHistory} setThreadHistory={setThreadHistory}
              voiceConfig={voiceConfig}
              spaceSession={spaceHook.activeSpaceInfo}
              onLeave={spaceHook.closeSpaceSession}
              onAskShift={null}
              onSidebarCollapse={setSidebarCollapsed}
            />
          )}
          {activeFeature === 'workspace' && spaceHook.activeSpaceInfo &&
            spaceHook.activeSpaceInfo?.workspace_metadata?.solo !== true && (
            <CollaborateRoom
              userId={userId} supabase={supabase} userRecord={userRecord}
              workspaceSession={spaceHook.activeSpaceInfo}
              voiceConfig={voiceConfig}
              onExit={spaceHook.closeSpaceSession}
              onSidebarCollapse={setSidebarCollapsed}
            />
          )}

          {/* Digest + Innovate routes deleted (Phase 3 + 4 cleanup). */}
          {activeFeature === 'playbook' && (
            <PlaybookFeature userId={userId} supabase={supabase}
              userRecord={userRecord} onClose={() => navigate('chat')} />
          )}
          {activeFeature === 'notes' && (
            <NotesFeature userId={userId} supabase={supabase}
              userRecord={userRecord} onNoteNavigate={actions.handleNoteNavigate}
              onClose={() => navigate('chat')} />
          )}
          {/* Ideas panel route deleted (Phase 1+2 cleanup). Captures
              flow into Notes with note_type='idea' to preserve the type. */}
          {activeFeature === 'signal' && (
            <IntelligenceFeature userId={userId} supabase={supabase} onDiscuss={(report) => {
              navigate('chat');
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('shift:submit-message', {
                  detail: { text: `Let's discuss this finding: "${report.title}" — ${report.body.slice(0, 200)}...` },
                }));
              }, 300);
            }} />
          )}

          {activeFeature === 'artifacts' && (
            <ArtifactLibrary key={artifactsRemountKey} userId={userId} supabase={supabase}
              userRecord={userRecord} onLoadThread={actions.handleLoadThread}
              onArtifactNavigate={actions.handleArtifactNavigate} onClose={() => navigate('chat')} />
          )}
          {activeFeature === 'intelligence' && (
            <DomainIntelligenceFeature
              userRecord={userRecord}
              onNavigateOut={navigate}
              onSidebarCollapse={setSidebarCollapsed}
            />
          )}
          {activeFeature === 'workstations' && (
            <WorkstationsFeature
              userId={userId}
              userRecord={userRecord}
              supabase={supabase}
              onSidebarCollapse={setSidebarCollapsed}
            />
          )}
          {activeFeature === 'projects' && (
            <ProjectsFeature
              userId={userId}
              userRecord={userRecord}
              voiceConfig={voiceConfig}
              onClose={() => navigate('chat')}
              onSidebarCollapse={setSidebarCollapsed}
            />
          )}
          {activeFeature === 'lab' && (
            <LabFeature session={session} supabase={supabase} userRecord={userRecord} />
          )}
          {activeFeature === 'profile' && (
            <ProfileFeature userId={userId} supabase={supabase} userRecord={userRecord} session={session} onClose={() => navigate('chat')} />
          )}
          {activeFeature === 'settings' && (
            <SettingsSlot
              session={session} supabase={supabase}
              userRecord={userRecord} setUserRecord={setUserRecord}
              settingsTab={settingsTab} setSettingsTab={setSettingsTab}
              threadHistory={threadHistory} setThreadHistory={setThreadHistory}
              onClose={() => navigate('chat')}
            />
          )}
        </div>

        <GlobalOverlays
          session={session} supabase={supabase} userRecord={userRecord}
          threadHistory={threadHistory} onLoadThread={actions.handleLoadThread}
          spaceHook={spaceHook}
          showSearch={showSearch} setShowSearch={setShowSearch}
          showInviteModal={showInviteModal} setShowInviteModal={setShowInviteModal}
          showCapture={showCapture} setShowCapture={setShowCapture}
        />

        {showReferralModal && (
          <ReferralModal
            userId={userId} supabase={supabase} userRecord={userRecord}
            onClose={() => setShowReferralModal(false)}
          />
        )}

        {showSoloModal && (
          <WorkspaceSoloModal
            onClose={() => setShowSoloModal(false)}
            onCreate={actions.handleSoloCreate}
            creating={creatingSolo}
          />
        )}
      </div>
    </>
  );
}
